import { type MeResponse } from '@diario/common';
import { Link } from '@tanstack/react-router';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { CapaModal } from '../../../components/ui/capaModal';

export interface MapaAppProps {
	/** Cierra el panel: lo invoca la ✕ propia, `CapaModal` (velo/`Escape`) y cada acceso al navegar. */
	onCerrar: () => void;
	/** Cierra la sesión activa; la invoca la entrada final del panel, con el panel ya cerrado. Se
	 * recibe siempre, aunque `Cabecera` solo monta este componente con sesión activa — mismo
	 * criterio que `MenuUsuarioProps.onCerrarSesion` antes de esta tarea (`menuUsuario.tsx`, retirado). */
	onCerrarSesion: () => void;
	/** Controla `CapaModal`: `false` no monta nada (velo, portal ni listeners), ver su JSDoc. */
	open: boolean;
	/** La sesión activa: de aquí salen las condiciones de rol de cada acceso — las mismas que usa
	 * `Inicio` para su rejilla «Lo que puedes hacer» (`puedeVerAcceso`, exportada de este mismo
	 * fichero, es la única fuente de esa regla). */
	sesion: MeResponse;
}

type ClaveAcceso =
	| 'bienvenida'
	| 'centro'
	| 'crearExperiencia'
	| 'enlaces'
	| 'experiencias'
	| 'instalar'
	| 'misExperiencias'
	| 'saberMas';
type RutaAcceso =
	| '/bienvenida'
	| '/centro'
	| '/crear-experiencia'
	| '/enlaces'
	| '/experiencias'
	| '/mis-experiencias'
	| '/saber-mas';

export interface AccesoDeMenu {
	clave: ClaveAcceso;
	/** Ancla dentro de `to` (fusión de `/instalar` en `/saber-mas` el 2026-09-15, ver
	 * `docs/decisions/ADR-0023-fusion-de-saber-mas-e-instalar.md`). Opcional:
	 * `inicio.tsx` consume este tipo sin conocer `hash` y sigue compilando sin tocarse. */
	hash?: string;
	to: RutaAcceso;
}

/**
 * Las ocho entradas de navegación del panel (ADR-0022): planes vivos, mis experiencias, crear
 * experiencia, enlaces, centro de mando, que Tuad enseñe la app, instalar como app, saber más —
 * antes de la salida final «cerrar sesión», que no es una entrada de esta lista porque no navega
 * a ningún sitio. Las seis últimas conservan el mismo orden y cálculo que `MenuUsuario` antes de
 * esta tarea (desplegable retirado, `menuUsuario.tsx` borrado). Se exporta para que `Inicio`
 * reutilice el mismo listado (filtrado a las que llevan descripción propia en `shell:accesos`)
 * sin duplicarlo.
 *
 * «Instalar como app» ya no es una ruta propia (fusión del 2026-09-15, ver ADR-0023): es un ancla
 * dentro de `/saber-mas` (`to: '/saber-mas', hash: 'instalar'`), en la misma posición que ocupaba
 * antes de la fusión.
 */
export const ACCESOS_DE_MENU: readonly AccesoDeMenu[] = [
	// Primera entrada, delante de "crear experiencia": `/experiencias` es el catálogo público
	// (iteración transversal «diseño 2026-09-16», `design-system/ui_kits/app/screens/planes.html`)
	// — descubrir los planes que ya existen es el paso natural antes de montar el tuyo propio.
	{ clave: 'experiencias', to: '/experiencias' },
	// P-14 (oleada «rescate navegación» 2026-09-17): "lo que organizas y a lo que vas" va justo
	// después del catálogo público — antes de montar el tuyo propio, lo natural es ver primero lo
	// que ya es tuyo. Exige sesión igual que el resto (`puedeVerAcceso`, sin condición de rol
	// propia: la pantalla la construye otro agente de esta misma oleada en `/mis-experiencias`).
	{ clave: 'misExperiencias', to: '/mis-experiencias' },
	{ clave: 'crearExperiencia', to: '/crear-experiencia' },
	{ clave: 'enlaces', to: '/enlaces' },
	{ clave: 'centro', to: '/centro' },
	{ clave: 'bienvenida', to: '/bienvenida' },
	{ clave: 'instalar', hash: 'instalar', to: '/saber-mas' },
	{ clave: 'saberMas', to: '/saber-mas' },
];

/**
 * Condición de rol de cada entrada — conservada tal cual de `MenuUsuario` (ADR-0022: "el cálculo
 * de qué accesos le tocan a quién no cambia, cambia el contenedor que los presenta") para las seis
 * entradas originales, y con la misma condición para `experiencias`/`misExperiencias`: ninguna de
 * las dos exige más que sesión (`misExperiencias`: "lo que organizas y a lo que vas" es igual de
 * visible para cualquier tier, no hay nada ahí que un `PENDING` no pueda ver de lo suyo), igual
 * que `crearExperiencia`/`bienvenida`/`instalar`/`saberMas`, todas siempre visibles con sesión (ya
 * la exige quien monta este panel); `enlaces` con
 * `systemRole === 'ADMIN'` o `canInviteOrganizers === true`;
 * `centro` con `systemRole === 'ADMIN'` o `tier === 'COLLABORATOR'` — misma comparación explícita
 * `=== true` que ya usa la guarda de `/enlaces` (documento 04 §7, RN-11) para que la ausencia del
 * permiso opcional signifique "sin permiso" sin depender de la coerción implícita de `undefined`.
 */
export const puedeVerAcceso = (clave: ClaveAcceso, sesion: MeResponse): boolean => {
	if (clave === 'enlaces') {
		return sesion.systemRole === 'ADMIN' || sesion.canInviteOrganizers === true;
	}
	if (clave === 'centro') {
		return sesion.systemRole === 'ADMIN' || sesion.tier === 'COLLABORATOR';
	}
	return true;
};

/**
 * "El mapa de la app" (ADR-0022): el panel lateral que sustituye al desplegable `MenuUsuario`,
 * montado sobre `CapaModal presentacion='lateral'` — velo, portal, foco atrapado/restituido,
 * `Escape` y bloqueo de scroll ya resueltos ahí, no reimplementados aquí
 * (`specs/diseno-2026-09-14/especificacion.md`). Los accesos son enlaces reales, no un `role='menu'`
 * de verdad: `CapaModal` solo atrapa `Tab` (no gestiona flechas como un menú ARIA), así que forzar
 * `role='menu'`/`menuitem` sería semánticamente falso — de ahí el nuevo nombre accesible y el nuevo
 * `role` que pide el criterio de aceptación de la tarea (antes: botón «Abrir menú» + `role='menu'`).
 */
export const MapaApp = ({ onCerrar, onCerrarSesion, open, sesion }: MapaAppProps): React.JSX.Element => {
	const { t } = useTranslation('shell');
	const idTitulo = useId();
	const accesosVisibles = ACCESOS_DE_MENU.filter(acceso => puedeVerAcceso(acceso.clave, sesion));

	/** Llamadas `t()` literales, una por clave — nunca indexando la clave `nav` con una plantilla de
	 * cadena a partir de `acceso.clave`: con `ClaveAcceso` cerrado y corto (las ocho entradas de
	 * `ACCESOS_DE_MENU`), el `Record` evita registrar un patrón nuevo en `preservePatterns` de
	 * `i18next.config.ts` (fichero fuera de la lista de esta tarea) — mismo molde que
	 * `fichaSolicitud.tsx` usa para los arquetipos (`docs/lessons.md` § «Una clave i18n indexada
	 * por variable no la ve el extractor, pero un `Record` de llamadas literales sí»). */
	const etiquetaPorAcceso: Record<ClaveAcceso, string> = {
		bienvenida: t('nav.bienvenida'),
		centro: t('nav.centro'),
		crearExperiencia: t('nav.crearExperiencia'),
		enlaces: t('nav.enlaces'),
		experiencias: t('nav.experiencias'),
		instalar: t('nav.instalar'),
		misExperiencias: t('nav.misExperiencias'),
		saberMas: t('nav.saberMas'),
	};

	return (
		<CapaModal className='px-6 pt-4 pb-6' onCerrar={onCerrar} open={open} presentacion='lateral' tituloId={idTitulo}>
			<div className='flex items-center justify-between gap-4 border-border border-b pb-4'>
				<h2 className='font-black font-display text-ink text-lg' id={idTitulo}>
					{t('mapa.titulo')}
				</h2>
				<button
					aria-label={t('mapa.cerrar')}
					className='inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-pill text-gray-ink hover:bg-paper-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink'
					onClick={onCerrar}
					type='button'
				>
					<span aria-hidden='true'>✕</span>
				</button>
			</div>
			<nav className='mt-2 flex flex-col gap-1'>
				{accesosVisibles.map(acceso => (
					<Link
						className='flex min-h-11 items-center rounded-sm px-3 py-2 text-ink motion-safe:transition-colors motion-safe:duration-fast hover:bg-paper-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink'
						hash={acceso.hash}
						key={acceso.clave}
						onClick={onCerrar}
						to={acceso.to}
					>
						{etiquetaPorAcceso[acceso.clave]}
					</Link>
				))}
			</nav>
			<div className='mt-6 border-border border-t pt-4'>
				<button
					className='flex min-h-11 w-full items-center rounded-sm px-3 py-2 text-left text-ink motion-safe:transition-colors motion-safe:duration-fast hover:bg-paper-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink'
					onClick={() => {
						onCerrar();
						onCerrarSesion();
					}}
					type='button'
				>
					{t('menu.cerrarSesion')}
				</button>
			</div>
		</CapaModal>
	);
};
